import React, { useState } from "react";
import { useSearchParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Leaf, Hand, Sparkles, FlaskConical, ShieldCheck, Package } from "lucide-react";
import LeadForm from "../components/LeadForm";
import { PRODUCT_CATEGORIES, CATALOG } from "../data/site";

const HIGHLIGHTS = [
  { Icon: Hand,         t: "Hand Block Printed", d: "Teak blocks, pressed by master artisans." },
  { Icon: Leaf,         t: "100% Cotton",        d: "Tested for colour fastness and GSM." },
  { Icon: FlaskConical, t: "Azo-Free Dyes",      d: "Low-impact colour chemistry, skin-safe." },
  { Icon: Sparkles,     t: "Artisan-Crafted",    d: "Generational workshop lineage." },
  { Icon: ShieldCheck,  t: "100% QC",            d: "Women-led piece-by-piece inspection." },
  { Icon: Package,      t: "Export-Ready",       d: "Labels, packaging, docs per market." },
];

export default function Products() {
  const [params] = useSearchParams();
  const cat = params.get("cat");
  // Redirect old ?cat= links to clean /products/:slug URLs
  if (cat) {
    const catMeta = PRODUCT_CATEGORIES.find((c) => c.key === cat);
    if (catMeta?.slug) return <Navigate to={`/products/${catMeta.slug}`} replace />;
  }
  return <CategoryIndex />;
}

function CategoryIndex() {
  return (
    <>
      <Helmet>
        <title>Products Catalogue | Indus Art — Hand Block Printed Textiles from Jaipur</title>
        <meta name="description" content="Browse 11 categories of hand block-printed cotton home textiles — bedding, table linen, bath, garments, living décor & more. Wholesale & export from Jaipur." />
        <meta property="og:title" content="Products Catalogue | Indus Art" />
        <meta property="og:description" content="11 categories, 90+ products. Hand block-printed cotton home textiles from Jaipur — wholesale & export." />
        <link rel="canonical" href="https://www.indusartjaipur.com/products" />
      </Helmet>
      <main data-testid="products-page" className="pt-28">
        <section className="py-16 md:py-24 bg-[#faf6ef]">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
            <div className="text-[#b4462a] text-xs tracking-[0.3em] uppercase font-bold mb-4">Products Catalogue</div>
            <h1 className="text-5xl md:text-7xl leading-[1.05] tracking-tight font-light">11 categories. One workshop.</h1>
            <p className="mt-6 text-lg text-[#1a1a1a]/70 max-w-2xl mx-auto leading-[1.8]">
              From bedsheets to bathrobes, table runners to tote bags — every piece hand block-printed in Jaipur.
            </p>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {PRODUCT_CATEGORIES.map((c, i) => (
                <motion.div
                  key={c.key}
                  initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, delay: i * 0.04 }}
                  data-testid={`cat-${c.key}`}
                >
                  <Link to={`/products/${c.slug}`} className="group block rounded-[2rem] overflow-hidden bg-cream-gradient shadow-sm hover:shadow-soft hover:scale-[1.01] transition-all cursor-pointer">
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img src={c.img} alt={c.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      {c.priority && (
                        <div className="absolute top-4 left-4 bg-[#d98f2f] text-[#1a1a1a] text-[10px] font-extrabold tracking-[0.2em] uppercase rounded-full px-3 py-1">Priority</div>
                      )}
                    </div>
                    <div className="p-6">
                      <div className="text-xl font-extrabold text-[#1e2a5a] mb-2">{c.name}</div>
                      <div className="text-sm text-[#1a1a1a]/65 leading-[1.6] mb-3">{c.tagline}</div>
                      {c.subcategories && (
                        <div className="flex flex-wrap gap-1.5 mb-4">
                          {c.subcategories.slice(0, 3).map((s) => (
                            <span key={s} className="text-[10px] bg-[#1e2a5a]/8 text-[#1e2a5a] rounded-full px-2.5 py-1">{s}</span>
                          ))}
                          {c.subcategories.length > 3 && (
                            <span className="text-[10px] text-[#1a1a1a]/40 rounded-full px-2.5 py-1">+{c.subcategories.length - 3} more</span>
                          )}
                        </div>
                      )}
                      <div className="inline-flex items-center gap-1.5 text-xs font-extrabold text-[#b4462a] group-hover:text-[#1e2a5a] transition">
                        Explore range <ArrowRight size={14} strokeWidth={2.4} className="group-hover:translate-x-1 transition" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}

// CategoryDetail kept for reference only — navigation now uses /products/:slug via ProductCategoryPage
function CategoryDetail({ cat }) {
  const [inquiry, setInquiry] = useState(null);
  const products = CATALOG[cat.key] || [];
  const idx = PRODUCT_CATEGORIES.findIndex((c) => c.key === cat.key);
  const prev = PRODUCT_CATEGORIES[(idx - 1 + PRODUCT_CATEGORIES.length) % PRODUCT_CATEGORIES.length];
  const next = PRODUCT_CATEGORIES[(idx + 1) % PRODUCT_CATEGORIES.length];

  return (
    <main data-testid={`category-page-${cat.key}`} className="pt-28">
      {/* Hero */}
      <section className="relative py-16 md:py-24 bg-indigo-gradient text-[#faf6ef] overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src={cat.img} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1e2a5a]/70 to-[#1e2a5a]/95" />
        </div>
        <div className="relative max-w-[1100px] mx-auto px-6 md:px-10">
          <Link to="/products" className="inline-flex items-center gap-2 text-[#d98f2f] text-xs font-extrabold tracking-[0.2em] uppercase mb-6 hover:text-[#faf6ef]" data-testid="back-to-products">
            <ArrowLeft size={14} strokeWidth={2.4} /> All Categories
          </Link>
          <div className="text-[#d98f2f] text-xs tracking-[0.3em] uppercase font-bold mb-4">Category · {String(idx + 1).padStart(2, "0")} / {PRODUCT_CATEGORIES.length}</div>
          <h1 className="text-5xl md:text-7xl leading-[1.05] tracking-tight font-light mb-6">{cat.name}</h1>
          <p className="text-lg md:text-xl text-[#faf6ef]/85 leading-[1.8] max-w-2xl">{cat.tagline}</p>
          <div className="mt-8 inline-flex items-center gap-3 glass-dark rounded-full px-5 py-3">
            <span className="text-[#d98f2f] font-extrabold text-2xl">{products.length}</span>
            <span className="text-sm text-[#faf6ef]/85">ready-to-quote products in this range</span>
          </div>
        </div>
      </section>

      {/* Category infographic: 6 common attributes */}
      <section className="py-16 md:py-20 bg-[#faf6ef]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="text-[#b4462a] text-xs tracking-[0.3em] uppercase font-bold mb-4 text-center">Common across every piece</div>
          <h2 className="text-3xl md:text-4xl leading-[1.1] font-light mb-10 text-center">Six things that never change.</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {HIGHLIGHTS.map((h, i) => (
              <motion.div key={h.t}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="rounded-3xl bg-white shadow-sm p-5 text-center"
              >
                <div className="w-12 h-12 mx-auto rounded-2xl bg-[#d98f2f]/15 text-[#b4462a] flex items-center justify-center mb-3">
                  <h.Icon size={20} strokeWidth={1.8} />
                </div>
                <div className="text-sm font-extrabold text-[#1e2a5a] mb-1 leading-tight">{h.t}</div>
                <div className="text-[11px] text-[#1a1a1a]/60 leading-[1.5]">{h.d}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Product list with spec pills */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="flex items-end justify-between mb-8">
            <h2 className="text-3xl md:text-4xl leading-[1.1] font-light">The range</h2>
            <div className="text-xs tracking-[0.2em] uppercase text-[#1a1a1a]/55 font-bold">{products.length} products</div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 md:gap-5">
            {products.map((p, i) => (
              <motion.div key={p.n}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.45, delay: (i % 6) * 0.04 }}
                className="rounded-3xl bg-cream-gradient p-5 md:p-6 flex items-start gap-5 group hover:shadow-soft transition-all"
                data-testid={`product-${cat.key}-${i}`}
              >
                <div className="shrink-0 w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center text-[#d98f2f] font-extrabold">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold text-[#1e2a5a] leading-snug mb-2">{p.n}</div>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {p.specs.map((s) => (
                      <span key={s} className="text-[10px] font-bold tracking-wide uppercase bg-white text-[#1a1a1a]/70 rounded-full px-2.5 py-1">{s}</span>
                    ))}
                  </div>
                  <button
                    onClick={() => setInquiry(p)}
                    data-testid={`inquire-${cat.key}-${i}`}
                    className="text-xs font-extrabold text-[#b4462a] hover:text-[#1e2a5a] inline-flex items-center gap-1.5 transition"
                  >
                    Inquire about this <ArrowRight size={12} strokeWidth={2.4} className="group-hover:translate-x-1 transition" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Prev / Next category nav */}
      <section className="py-12 bg-[#faf6ef] border-t border-[#1a1a1a]/10">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 grid sm:grid-cols-2 gap-4">
          <Link to={`/products?cat=${prev.key}`} data-testid="prev-category" className="group rounded-3xl bg-white p-5 shadow-sm hover:shadow-soft transition-all flex items-center gap-4">
            <ArrowLeft size={20} strokeWidth={2} className="text-[#b4462a] group-hover:-translate-x-1 transition" />
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#1a1a1a]/55 font-bold">Previous</div>
              <div className="font-extrabold text-[#1e2a5a]">{prev.name}</div>
            </div>
          </Link>
          <Link to={`/products?cat=${next.key}`} data-testid="next-category" className="group rounded-3xl bg-white p-5 shadow-sm hover:shadow-soft transition-all flex items-center justify-end gap-4 text-right">
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#1a1a1a]/55 font-bold">Next</div>
              <div className="font-extrabold text-[#1e2a5a]">{next.name}</div>
            </div>
            <ArrowRight size={20} strokeWidth={2} className="text-[#b4462a] group-hover:translate-x-1 transition" />
          </Link>
        </div>
      </section>

      {/* Inquiry CTA band */}
      <section className="py-20 bg-madder-gradient text-[#faf6ef]">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 text-center">
          <h2 className="text-4xl md:text-5xl leading-[1.1] font-light mb-4">Interested in {cat.name}?</h2>
          <p className="text-[#faf6ef]/85 mb-8 max-w-xl mx-auto leading-[1.8]">Share your brief — MOQ, market, design preference — and we'll come back within 24 hours with a personalised quote.</p>
          <Link to="/contact#lead-form" data-testid="category-inquiry-cta" className="btn-pill bg-[#faf6ef] text-[#1e2a5a] hover:bg-[#1e2a5a] hover:text-[#faf6ef] shadow-cta py-4 px-8 text-sm">
            Request a Quote <ArrowRight size={16} strokeWidth={2.2} />
          </Link>
        </div>
      </section>

      {/* Inquiry modal */}
      {inquiry && (
        <div className="fixed inset-0 z-50 bg-[#1a1a1a]/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="inquiry-modal">
          <div className="bg-[#faf6ef] rounded-[2rem] max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
            <button onClick={() => setInquiry(null)} data-testid="close-inquiry" className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/80 hover:bg-white flex items-center justify-center z-10 shadow-sm" aria-label="Close">✕</button>
            <div className="p-8 md:p-10">
              <div className="text-[#b4462a] text-xs tracking-[0.3em] uppercase font-bold mb-3">Inquiry · {cat.name}</div>
              <h3 className="text-2xl md:text-3xl text-[#1e2a5a] font-light mb-3">{inquiry.n}</h3>
              <div className="flex flex-wrap gap-1.5 mb-6">
                {inquiry.specs.map((s) => (
                  <span key={s} className="text-[10px] font-bold tracking-wide uppercase bg-white text-[#1a1a1a]/70 rounded-full px-2.5 py-1">{s}</span>
                ))}
              </div>
              <LeadForm
                source={`product_${cat.key}`}
                prefill={{ message: `Interested in: ${inquiry.n}\nSpecs: ${inquiry.specs.join(" · ")}`, categories: [cat.name] }}
              />
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
